'use client';

import { type Theme, type ThemeOptions, createTheme, responsiveFontSizes } from '@mui/material/styles';

import { btmColors } from './colors';
import { overrideButtonStyles } from './overrides/button';
import { dmSans } from './font';

declare module '@mui/material/styles' {
    interface AdditionalColors {
        secondaryBackground: string;
        labelColor: string;
        baseColor: string;
        baseBackground: string;
    }

     
    interface Theme {
        colors: AdditionalColors;
    }

     
    interface ThemeOptions {
        colors: AdditionalColors;
    }
}

export const baseThemeOptions: ThemeOptions = {
    colors: {
        secondaryBackground: btmColors.lightBlue,
        labelColor: btmColors.gray,
        baseColor: btmColors.white,
        baseBackground: btmColors.blue,
    },
    palette: {
        primary: {
            main: btmColors.navy,
            dark: btmColors.green,
            contrastText: btmColors.white,
        },
        secondary: {
            main: btmColors.blue,
            light: btmColors.navy,
            dark: btmColors.green,
            contrastText: btmColors.white,
        },
        info: {
            main: btmColors.cool_gray,
        },
        success: {
            main: '#009747',
        },
        warning: {
            main: btmColors.orange,
        },
        error: {
            main: '#d50000',
        },
        text: {
            primary: btmColors.primaryText,
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 4,
    },
    typography: {
        fontFamily: [dmSans.style.fontFamily, 'Arial', 'sans-serif'].join(','),
        fontSize: 16,
        h1: {
            fontSize: 40,
            lineHeight: 2.625,
            fontWeight: 700,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.black,
        },
        h2: {
            fontSize: 32,
            lineHeight: 2,
            fontWeight: 600,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.navy,
        },
        h3: {
            fontSize: 24,
            lineHeight: 1.5,
            fontWeight: 600,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.black,
        },
        h4: {
            fontSize: 22,
            lineHeight: 1.375,
            fontWeight: 700,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.navy,
        },
        h5: {
            fontSize: 20,
            lineHeight: 1.25,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.black,
        },
        h6: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,
            fontFamily: dmSans.style.fontFamily,
            color: btmColors.navy,
        },
        body1: {
            fontSize: 16,
            lineHeight: 1.5,
            fontFamily: dmSans.style.fontFamily,
        },
        body2: {
            fontSize: 14,
            lineHeight: 1.43,
            fontFamily: dmSans.style.fontFamily,
        },
        caption: {
            fontSize: 12,
            lineHeight: 1.66,
            fontFamily: dmSans.style.fontFamily,
        },
        button: {
            fontSize: 16,
            fontWeight: 700,
            fontFamily: dmSans.style.fontFamily,
            textTransform: 'none' as const,
        },
    },
    components: {
        MuiFormLabel: {
            styleOverrides: {
                asterisk: {
                    color: '#db3131',
                    '&$error': {
                        color: '#db3131',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: btmColors.b2b_blue,
                    textDecoration: 'none',
                    '&:hover': {
                        color: btmColors.navy,
                        textDecoration: 'underline',
                    },
                },
            },
            defaultProps: {
                underline: 'hover',
            },
        },
        // TODO should be handling zIndex in a more consistent way (functionally)
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    zIndex: 8,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    zIndex: 10,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '13px',
                    height: '46px',
                },
            },
        },
    },
};

const compose =
    (...functions: ((theme: Theme) => Theme)[]) =>
    (args: Theme) =>
        functions.reduceRight((arg, fn) => fn(arg), args);

const baseTheme = createTheme(baseThemeOptions);

export const theme = compose(responsiveFontSizes, overrideButtonStyles)(baseTheme);
